import {
    DATE_TIME_FORMAT_NAME_MODEL, formatMinutesToHoursMinutes, getDateFormat,
    getTimeFormat
} from "../../../../../Apis/functions";
import { TimePickerChip } from "../../../../../components/buttons/IconButtons";
import CustomChip from "../../../../../components/CustomChip";
import {getAttendanceColorByStatus} from "../Models/Model";
import {getAccessMethodById} from "../../../../../enums/accessMethodEnum";
import React from "react";
import CustomTooltip from "../../../../../components/CustomTooltip";
import {useTranslation} from "react-i18next";
import AccessMethodIcon from "../../../../../components/icons/AccessMethodIcon";
import {TYPE_MODEL_COLOR_MODEL} from '../../../Company/Payroll/Model/Model';

/**
 * @description
 * EntryExitOption renders a compact visual summary of entry and exit data for a person,
 * including device names, timestamps, access methods (with icons), and total time.
 * It uses color-coded chips based on status and optionally includes action buttons.
 *
 * @Author: Karen Yunuen Pozos Arias
 * @JobTitle: Software Developer
 * @Email: kpozos@ibl.mx
 *
 * @param {Object} props - Component props.
 * @param {Object} props.record - Record data containing entry, exit, and total time information.
 * @param {Function} [props.getActionsButtons] - Optional function to render additional action buttons.
 * @param {string} props.color - Base color used to determine the chip and status colors.
 * @param {string} props.model - Model used to retrieve variables for setting the color.
 *
 * @returns {JSX.Element} A component displaying entry and exit info with visual cues and optional actions.
 */

const EntryExitOption = ({ record, getActionsButtons = null, color,model }) => {

    const {t} = useTranslation();
    let entryBackgroundColor = getAttendanceColorByStatus(record?.entryDateStatus, color, model);
    let entryBackgroundColorTxt = getAttendanceColorByStatus(record?.entryDateStatus, color, model,TYPE_MODEL_COLOR_MODEL.TXT_COLOR);
    let exitBackgroundColor = getAttendanceColorByStatus(record?.exitDateStatus, color, model);
    let exitBackgroundColorTxt = getAttendanceColorByStatus(record?.exitDateStatus, color, model,TYPE_MODEL_COLOR_MODEL.TXT_COLOR);
    let totalBackgroundColor = getAttendanceColorByStatus(record?.totalTimeStatus, color, model);
    let totalBackgroundColorTxt = getAttendanceColorByStatus(record?.totalTimeStatus, color, model,TYPE_MODEL_COLOR_MODEL.TXT_COLOR);

    const entryAccessMethod = getAccessMethodById(record?.entryAccessMethodId);
    const exitAccessMethod = getAccessMethodById(record?.exitAccessMethodId);
 
    return (
        <div className="entry-exit-option body-content w-100">
            <div className={`record-col in px-2`}>
                <div className="access-block">
                    <div className={"w-120px"}>
                        <CustomTooltip
                            text={record.entryDeviceName}
                            classes={"font-color-general-950 ibl-font-w-500 fs-14px text-ellipsis-1-row"}
                            showIfTruncated={true}
                            placement={"top"}
                        />
                    </div>
                    <div className="access-content">
                        {entryAccessMethod?.icon &&
                            <CustomTooltip text={t(entryAccessMethod.value)}>
                               <p><AccessMethodIcon fill={entryBackgroundColorTxt} AccessMethodId={record?.entryAccessMethodId}/></p>
                            </CustomTooltip>
                        }
                        <div className='d-flex flex-column align-items-center text-center gap-2'>
                            <CustomChip
                                background={entryBackgroundColor}
                                fontColor={entryBackgroundColorTxt}
                                legend=
                                     {record.entryDate ? (
                                        <div className='d-flex flex-column text-center'>
                                            <span>
                                                {getDateFormat(
                                                    record.entryDate,
                                                    false,
                                                    DATE_TIME_FORMAT_NAME_MODEL.slash,
                                                )}
                                            </span>
                                            <span>{getTimeFormat(record.entryDate, true)}</span>
                                        </div>
                                    ) : (
                                        <span>{t('no_record')}</span>
                                    )}
                                className={`w-120px ${record.entryDate ? 'h-40px' : ''}`}
                            />
                        </div>
                    </div>
                </div>
            </div>
            <div className="vertical-separator-shades-950 h-71px background-shades-200" />
            <div className="record-col out px-2">
                <div className="access-block">
                    <div className={"w-120px"}>
                        <CustomTooltip
                            text={record.exitDeviceName}
                            classes={"font-color-general-950 ibl-font-w-500 fs-14px text-ellipsis-1-row "}
                            showIfTruncated={true}
                            placement={"top"}
                        />
                    </div>

                    <div className="access-content">
                        {exitAccessMethod?.icon &&
                            <CustomTooltip text={t(exitAccessMethod.value)}>
                              <p><AccessMethodIcon fill={exitBackgroundColorTxt} AccessMethodId={record?.exitAccessMethodId}/></p>
                            </CustomTooltip>
                        }
                        <div className='d-flex flex-column align-items-center text-center gap-2'>
                            <CustomChip
                                background={exitBackgroundColor}
                                fontColor={exitBackgroundColorTxt}
                                legend={
                                    record.exitDate ? (
                                        <div className='d-flex flex-column text-center'>
                                            <span>
                                                {getDateFormat(
                                                    record.exitDate,
                                                    false,
                                                DATE_TIME_FORMAT_NAME_MODEL.slash
                                                )}
                                            </span>
                                            <span>{getTimeFormat(record.exitDate)}</span>
                                        </div>
                                    ) : (
                                        <span>{t('no_record')}</span>
                                    )
                                }
                                className={`w-120px ${record.exitDate ? 'h-40px' : ''}`}
                            />
                        </div>
                    </div>
                </div>
            </div>


            <div className="record-col center-symbol">
                =
            </div>

            <div className={`record-col duration pr-2 ${record.entryAccessMethodId ? "br-divider" : ""}`}>
                <CustomChip
                    icon={record.totalTime ? <TimePickerChip fill={totalBackgroundColorTxt} /> : undefined}
                    background={totalBackgroundColor}
                    fontColor={totalBackgroundColorTxt}
                    legend={
                        record?.totalTime != null
                            ? formatMinutesToHoursMinutes(record.totalTime)
                            : t('no_record')
                    }
                    className='w-125px'
                />
            </div>

            <div className="record-col align-content-center">
                { getActionsButtons ? <>
                    <div className="vertical-separator-shades-950 h-71px background-shades-200 m-0" />
                        {getActionsButtons()}
                </> :
                    <div className={"w-40px"}/>
                }
            </div>
        </div>
    );
};

export default EntryExitOption;
